import React, { Fragment, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import get from 'lodash/get';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import { withNamespaces } from 'react-i18next';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import '../../../../styles/themes/slick-themes.css';

import Box from '../../../components/molecules/Box';
import Picture from '../../../components/atoms/Picture';
import Grid from '../../../components/molecules/Grid';
import Slide from './Slide';
import carouselSettings from './CarouselSettings';
import useStyles from './style';
import {
  pushAnaylyticsData,
  createDynamicLabels,
} from '../../../../utils/analytics';
import { analyticsData } from '../../../../constants/Analytics/analytics-data';
import aspectRatios from '../../../utils/AspectRatios';

const HeroCarousel = ({
  carouselItems,
  getCurrentSlidePos,
  isMobile,
  hasThumbnail,
  carouselConfig,
  className,
  titleText,
  sitecoreContext,
  CustomComponent, // It can be any type of component like Cards or custom component
  thumbnailNeeded,
  componentName,
  pageName,
  handleOpenState,
  shouldShowThumb,
  isParalax = true,
  aspectRatio = '16x9-m',
  ...other
}) => {
  const classes = useStyles();
  const slides = carouselItems;
  let thumbnailImageExists = false;
  const [hidden, setHidden] = useState(false);
  const sliderElm = useRef(null);

  const posterImages =
    slides &&
    slides.length > 0 &&
    slides
      .map(slide => {
        if (get(slide, 'fields.video.value.href')) {
          return get(slide, 'fields.image.value.src');
        }
        return null;
      })
      .filter(slide => slide !== null);

  let settings = carouselSettings(carouselConfig);
  const addAnalyticsInfo = index => {
    const pushData =
      get(slides[index], 'fields.video.value.href', '').length > 0
        ? get(analyticsData, 'pageBodyInteraction.galleryVideoClick')
        : get(analyticsData, 'pageBodyInteraction.galleryImageClick');
    const obj = {
      ...pushData,
      ...{
        label: createDynamicLabels([
          pageName,
          componentName,
          `${get(sitecoreContext, 'route.name', '')}-${titleText}`,
        ]),
      },
    };
    pushAnaylyticsData(obj);
  };
  const addSwipeAnalyticsInfo = () => {
    const obj = {
      ...get(analyticsData, 'pageBodyInteraction.swipeArrowClick'),
      ...{
        label: createDynamicLabels([pageName, componentName, `${titleText}`]),
      },
    };
    pushAnaylyticsData(obj);
  };

  if (hasThumbnail) {
    settings = {
      ...settings,
      // eslint-disable-next-line react/display-name
      customPaging: i => {
        return (
          <a href="#">
            <Picture
              onClick={() => addAnalyticsInfo(i)}
              media={{
                image:
                  get(slides[i], 'fields.thumbnailImage') ||
                  get(slides[i], 'fields.image'),
                aspectRatio: aspectRatios['3x2'],
              }}
              isParalax={isParalax}
              alternateAltText={titleText && titleText.value}
            />
          </a>
        );
      },
    };
  }

  const renderSlider = () => {
    return (
      <Slider
        beforeChange={(currentSlide, nextSlide) => {
          setTimeout(() => {
            getCurrentSlidePos(nextSlide + 1);
            addSwipeAnalyticsInfo();
          }, 500);
        }}
        {...settings}
        {...other}
        ref={sliderElm}
      >
        {slides &&
          slides.map((item, i) => {
            const GenericComponent = CustomComponent;
            return (
              <Fragment key={i}>
                {!!GenericComponent ? (
                  <GenericComponent {...item} />
                ) : (
                  <Slide
                    titleText={titleText}
                    slide={item}
                    posterImages={posterImages}
                    componentName={componentName}
                    pageName={pageName}
                    aspectRatio={aspectRatio}
                    handleOpenState={handleOpenState}
                  />
                )}
              </Fragment>
            );
          })}
      </Slider>
    );
  };

  const renderSliderOnClick = i => {
    addAnalyticsInfo(i);
    setHidden(true);
    getCurrentSlidePos(i + 1);
    sliderElm.current.slickGoTo(i, true);
  };

  return (
    <Box component="div" className={clsx(classes.root, className)}>
      {thumbnailNeeded && isMobile && !hidden && slides && slides.length > 1 && !shouldShowThumb && (
        <div className={classes.thumbnails}>
          <Grid container>
            {slides &&
              slides.map((slide, i) => {
                if (
                  thumbnailImageExists ||
                  get(slide, 'fields.thumbnailImage.value.src', '')
                ) {
                  thumbnailImageExists = true;
                }
                return (
                  <Grid item xs={3} className="item" key={i}>
                    <a href="#" onClick={() => renderSliderOnClick(i)}>
                      <Picture
                        media={{
                          image: get(slide, 'fields.thumbnailImage', {}),
                          aspectRatio: aspectRatios['3x2'],
                        }}
                        isParalax={isParalax}
                        className="thumbnailImg"
                        alternateAltText={titleText}
                      />
                    </a>
                  </Grid>
                );
              })}
          </Grid>
        </div>
      )}
      <div
        className={
          hidden ||
            !thumbnailNeeded ||
            !thumbnailImageExists ||
            (slides && slides.length === 1)
            ? 'slider-wrapper'
            : ''
        }
      >
        {renderSlider()}
      </div>
    </Box>
  );
};

HeroCarousel.propTypes = {
  className: PropTypes.string,
  hasThumbnail: PropTypes.bool,
  isMobile: PropTypes.bool,
  carouselConfig: PropTypes.object,
  getCurrentSlidePos: PropTypes.func,
  titleText: PropTypes.string,
  thumbnailNeeded: PropTypes.bool,
  handleOpenState: PropTypes.bool,
};

HeroCarousel.defaultProps = {
  className: '',
  hasThumbnail: false,
  isMobile: false,
  carouselConfig: {},
  getCurrentSlidePos: () => { },
  titleText: '',
  thumbnailNeeded: true,
  handleOpenState: false,
};

export default withSitecoreContext()(withNamespaces()(HeroCarousel));
